import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Card,
  CardContent,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Pencil, Plus, SlidersHorizontal, Trash2, X } from 'lucide-react';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import { CustomPrompt } from '@/types';

export default function CustomPromptsPage() {
  const theme = useTheme();
  const [prompts, setPrompts] = useState<CustomPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPrompt, setEditPrompt] = useState<Partial<CustomPrompt>>({});
  const [deleteTarget, setDeleteTarget] = useState<CustomPrompt | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const res = await sentinelNetwork.get('/custom-prompts');
      setPrompts(res.data || []);
    } catch (err) {
      console.error('Failed to fetch prompts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (prompt: CustomPrompt) => {
    try {
      await sentinelNetwork.put(`/custom-prompts/${prompt.promptId}`, {
        enabled: !prompt.enabled,
      });
      fetchPrompts();
    } catch (err) {
      console.error('Failed to toggle prompt:', err);
    }
  };

  const handleSave = async () => {
    try {
      if (editPrompt.promptId) {
        await sentinelNetwork.put(`/custom-prompts/${editPrompt.promptId}`, editPrompt);
      } else {
        await sentinelNetwork.post('/custom-prompts', editPrompt);
      }
      setDialogOpen(false);
      setEditPrompt({});
      fetchPrompts();
      setSnackbar({ open: true, message: 'Question saved successfully', severity: 'success' });
    } catch (err) {
      console.error('Failed to save prompt:', err);
      setSnackbar({ open: true, message: 'Failed to save question', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await sentinelNetwork.delete(`/custom-prompts/${deleteTarget.promptId}`);
      setDeleteTarget(null);
      fetchPrompts();
      setSnackbar({ open: true, message: 'Question deleted successfully', severity: 'success' });
    } catch (err) {
      console.error('Failed to delete prompt:', err);
      setSnackbar({ open: true, message: 'Failed to delete question', severity: 'error' });
    }
  };

  const openCreateDialog = () => {
    setEditPrompt({
      label: '',
      questionText: '',
      outputKey: '',
      outputType: 'string',
      category: 'overview',
      instructions: '',
    });
    setDialogOpen(true);
  };

  const openEditDialog = (prompt: CustomPrompt) => {
    setEditPrompt({ ...prompt });
    setDialogOpen(true);
  };

  const categoryColor = (cat: string) => {
    const map: Record<string, 'info' | 'warning' | 'success' | 'error'> = {
      security: 'error',
      threat: 'error',
      overview: 'info',
      technical: 'warning',
      epa: 'success',
      'security-profile': 'warning',
      integration: 'success',
      operations: 'info',
    };
    return map[cat] || 'default';
  };

  const ON_DEMAND_CATEGORIES = new Set(['epa', 'security-profile', 'wiki']);
  const getScanScope = (cat: string) => ON_DEMAND_CATEGORIES.has(cat) ? 'On-Demand' : 'Per-Repo';

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%" width="100%">
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex' }}>
              <SlidersHorizontal size={24} color={theme.palette.primary.main} />
            </Box>
            <Typography variant="h5" fontWeight={300}>
              Extraction Questions
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
            Manage the questions used for transition intelligence extraction
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          onClick={openCreateDialog}
          sx={{ height: 40, fontWeight: 600 }}
        >
          Add Question
        </Button>
      </Stack>

      {prompts.length > 0 ? (
        <Card variant="outlined" elevation={0}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ maxHeight: 'calc(100vh - 350px)' }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Label</strong></TableCell>
                    <TableCell><strong>Output Key</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell align="center"><strong>Enabled</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {prompts.map((prompt) => (
                    <TableRow key={prompt.promptId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{prompt.label}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {prompt.outputKey}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={prompt.outputType}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: theme.palette.info.main,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip
                            label={prompt.category}
                            size="small"
                            color={categoryColor(prompt.category) as any}
                            variant="outlined"
                          />
                          <Chip
                            label={getScanScope(prompt.category)}
                            size="small"
                            sx={{
                              bgcolor: getScanScope(prompt.category) === 'Per-Repo'
                                ? alpha(theme.palette.primary.main, 0.1)
                                : alpha(theme.palette.warning.main, 0.1),
                              color: getScanScope(prompt.category) === 'Per-Repo'
                                ? theme.palette.primary.main
                                : theme.palette.warning.main,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={prompt.enabled}
                          onChange={() => handleToggleEnabled(prompt)}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <IconButton size="small" onClick={() => openEditDialog(prompt)}>
                            <Pencil size={18} />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(prompt)}>
                            <Trash2 size={18} />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No extraction questions configured
        </Typography>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex' }}>
                <Pencil size={24} color={theme.palette.primary.main} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                {editPrompt.promptId ? 'Edit Question' : 'New Question'}
              </Typography>
            </Stack>

            <TextField
              label="Label"
              value={editPrompt.label || ''}
              onChange={(e) => setEditPrompt({ ...editPrompt, label: e.target.value })}
              fullWidth
              size="small"
            />
            <TextField
              label="Question Text"
              value={editPrompt.questionText || ''}
              onChange={(e) => setEditPrompt({ ...editPrompt, questionText: e.target.value })}
              fullWidth
              multiline
              rows={4}
              size="small"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Output Key"
                value={editPrompt.outputKey || ''}
                onChange={(e) => setEditPrompt({ ...editPrompt, outputKey: e.target.value })}
                size="small"
                sx={{ flex: 1 }}
                disabled={!!editPrompt.promptId}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={editPrompt.outputType || 'string'}
                  label="Type"
                  onChange={(e) => setEditPrompt({ ...editPrompt, outputType: e.target.value })}
                >
                  <MenuItem value="string">string</MenuItem>
                  <MenuItem value="array">array</MenuItem>
                  <MenuItem value="object">object</MenuItem>
                  <MenuItem value="boolean">boolean</MenuItem>
                  <MenuItem value="number">number</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={editPrompt.category || 'security'}
                  label="Category"
                  onChange={(e) => setEditPrompt({ ...editPrompt, category: e.target.value })}
                >
                  <MenuItem value="security">security (Per-Repo)</MenuItem>
                  <MenuItem value="threat">threat (Per-Repo)</MenuItem>
                  <MenuItem value="overview">overview (Per-Repo)</MenuItem>
                  <MenuItem value="technical">technical (Per-Repo)</MenuItem>
                  <MenuItem value="epa">epa (On-Demand)</MenuItem>
                  <MenuItem value="security-profile">security-profile (On-Demand)</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <TextField
              label="Instructions (optional)"
              value={editPrompt.instructions || ''}
              onChange={(e) => setEditPrompt({ ...editPrompt, instructions: e.target.value })}
              fullWidth
              multiline
              rows={2}
              size="small"
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setDialogOpen(false)}
                startIcon={<X size={18} />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                startIcon={<Plus size={18} />}
                sx={{ fontWeight: 600 }}
              >
                {editPrompt.promptId ? 'Update' : 'Create'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.1), display: 'flex' }}>
                <Trash2 size={24} color={theme.palette.error.main} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Delete Question?
              </Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Are you sure you want to delete this question? This action cannot be undone.
            </Typography>

            {deleteTarget && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.error.main, 0.05),
                  border: '1px solid',
                  borderColor: alpha(theme.palette.error.main, 0.2),
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {deleteTarget.label}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }} color="text.secondary">
                  {deleteTarget.outputKey}
                </Typography>
              </Box>
            )}

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setDeleteTarget(null)}
                startIcon={<X size={18} />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                startIcon={<Trash2 size={18} />}
              >
                Delete
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
